import { MEMBER_PROFILE_SCREEN } from '../constants/category';
import * as ConstantMemberType from '../constants/memberTypesRegister';
import * as utils from '../functions/utils';
import DealBreaker from './dealBreaker';
import { MEMBER_LEVEL } from '../constants/constants';
import {
  ICountry,
  IMainPhoto,
  IMemberProfile,
  IMemberProfileDetail,
  IMemberProfileIntersection,
  IMemberProfileSeeking,
  IMemberTypeTitle,
} from 'redhotpie-shared-lib/interfaces/IUser';
import { ISeekingType } from 'redhotpie-shared-lib/types/clubProfile';

const buildProperties = (property: any) => {
  return {
    categoryId: property.categoryId,
    categoryLabel: property.categoryLabel,
    items: property.items.map((item: any) => ({
      ...item,
      value: '',
      value2: '',
    })),
  };
};

const upcaseFirstCharacter = (tmp: string) => tmp.charAt(0).toUpperCase() + tmp.substr(1);

export default class MemberProfile {
  detail: IMemberProfileDetail;
  profileIntersection: IMemberProfileIntersection;
  profileSeeking: IMemberProfileSeeking;
  mainPhotoSummary: IMainPhoto;
  listItem: {
    itemDate: string;
  };
  isNew: boolean;
  isProfileNotFound: boolean;

  constructor() {
    this.detail = {
      isCouple: false,
      userId: undefined,
      userName: '',
      memberType: 0,
      memberTypeTitle: undefined,
      age1: undefined,
      age2: undefined,
      ageTitle: '',
      location: '',
      stateName: '',
      countryName: '',
      isOnline: false,
      memberLevel: '',
      memberLevelValue: undefined,
      isDiamond: false,
      isVerified: false,
      introBody: '',
      introTitle: '',
      phoneNumber: '',
      phoneNumberShowUpgrade: false,
      properties: Object.values(MEMBER_PROFILE_SCREEN.PROFILES).map(item => buildProperties(item)),
      isPhoneVerified: false,
      isSmsOnly: false,
      generalInterests: [],
      sexualInterests: [],
      sexualPreferences: [],
      fetishInterests: [],
      fantasyActivity: {},
      fantasyLocation: {},
      fantasyMustHave: {},
      xmppId: '',
      country: undefined,
      profileInterests: {},
      isViewable: false,
      shareProfileCode: '',
    };
    this.profileIntersection = {
      isFriend: false,
      hasFriendRequest: false,
      hasFriendRequestDeleted: false,
      hasValidationRequest: false,
      isMatch: false,
      isLiked: false,
      isLikedMutual: false,
      isFavorited: false,
      isFavouritedViewer: false,
      isFavoritedMutual: false,
      hasMessages: false,
      canAddValidation: false,
      canViewValidations: false,
      hasFavouritedViewer: false,
      hasLikedViewer: false,
      dealBreakerConflict: [],
      dealBreakerConflictViewer: [],
    };
    this.profileSeeking = {
      seekingBody: '',
      stateID: '',
      stateName: '',
      ageMin: 0,
      ageMax: 0,
      memberTypes: [],
      intention: [],
      relationshipStatus: undefined,
      dealBreaker: [],
    };
    this.mainPhotoSummary = {
      photoID: undefined,
      height: null,
      width: null,
      isUserImage: false,
      isPublic: false,
      classification: undefined,
      thumbUrl: '',
      fullUrl: '',
      imageType: '',
      imageActionType: '',
      showSafeMode: false,
    };
    this.listItem = {
      itemDate: '',
    };
    this.isNew = false;
    this.isProfileNotFound = false;
  }

  static mappingPropertyObject(object: any): ISeekingType {
    return {
      value: object ? object.Value : '',
      text: object ? object.Text : '',
      textAbbr: object ? object.TextAbbr : '',
      isMatch: object ? object.IsMatch : false,
    };
  }

  static mappingMemberTypeTitle(object: any): IMemberTypeTitle {
    return {
      abbr: object ? object.Abbr : '',
      singular: object ? object.Singular : '',
      plural: object ? object.Plural : '',
    };
  }

  static mappingMemberCountry(object: any): ICountry {
    return {
      id: object ? object.CountryID : '',
      name: object ? object.Name : '',
      googleMapId: object ? object.GoogleMapPlacesId : '',
      code: object ? object.Code : '',
    };
  }

  static mappingStructureData(properties: any[], data: any, isCouple: boolean) {
    return properties.map(group => {
      const newItems = group.items.map((item: any) => {
        const propertyName = upcaseFirstCharacter(item.name);
        return (data[propertyName] && data[propertyName].Value > 0) || (data[`${propertyName}2`] && data[`${propertyName}2`].Value > 0)
          ? {
              ...item,
              value: data[propertyName] ? MemberProfile.mappingPropertyObject(data[propertyName]) : {},
              value2: isCouple && data[`${propertyName}2`] ? MemberProfile.mappingPropertyObject(data[`${propertyName}2`]) : {},
            }
          : {
              ...item,
              value: MemberProfile.mappingPropertyObject({}),
              value2: MemberProfile.mappingPropertyObject({}),
            };
      });

      return { ...group, items: newItems };
    });
  }

  static mappingCommonInterest(interestArr: any[]) {
    // isMatch
    if (interestArr.length <= 0) {
      return { common: [], unCommon: [] };
    }
    const mappingModel = interestArr.map(item => MemberProfile.mappingPropertyObject(item));
    const common: any[] = [];
    const unCommon: any[] = [];
    mappingModel.forEach(idx => {
      if (idx.isMatch) {
        common.push(idx);
      } else {
        unCommon.push(idx);
      }
    });
    return { common, unCommon };
  }

  static mappingObject(user: any) {
    // console.log(user)
    const { Detail, Seeking, Intersection, MainPhoto, ListItem, IsNew } = user;
    const isCouple =
      ConstantMemberType.MEMBER_TYPE_GROUPS[Detail.MemberType] === ConstantMemberType.GROUP_SINGLE_OR_COUPLES.COUPLE ||
      ConstantMemberType.MEMBER_TYPE_GROUPS[Detail.MemberType] === ConstantMemberType.GROUP_SINGLE_OR_COUPLES.COUPLE_MM ||
      ConstantMemberType.MEMBER_TYPE_GROUPS[Detail.MemberType] === ConstantMemberType.GROUP_SINGLE_OR_COUPLES.COUPLE_FF;

    const profile = new MemberProfile();
    profile.detail = {
      isCouple,
      userId: Detail.UserID,
      xmppId: Detail.UserID.toString(),
      userName: Detail.Username,
      memberType: Detail.MemberType,
      memberTypeTitle: MemberProfile.mappingMemberTypeTitle(Detail.MemberTypeTitle),
      age1: Detail.Age1,
      age2: Detail.Age2,
      ageTitle: Detail.AgeTitle,
      stateName: Detail.StateName,
      location: Detail.Location,
      countryName: Detail.CountryName,
      isOnline: Detail.IsOnline,
      memberLevel: Detail.MemberLevelValue
        ? Detail.MemberLevelValue.Text === MEMBER_LEVEL.PLATINUM && Detail.IsDiamond
          ? MEMBER_LEVEL.DIAMOND
          : Detail.MemberLevelValue.Text
        : '',
      memberLevelValue: MemberProfile.mappingPropertyObject(Detail.MemberLevelValue),
      isDiamond: Detail.IsDiamond,
      isVerified: Detail.IsVerified,
      introBody: utils.convertEnterStringToTagHTML(Detail.IntroBody),
      introTitle: Detail.IntroTitle,
      phoneNumber: Detail.PhoneNumber,
      isPhoneVerified: Detail.IsPhoneVerified,
      phoneNumberShowUpgrade: Detail.PhoneNumberShowUpgrade,
      isSmsOnly: Detail.IsSmsOnly,
      properties: MemberProfile.mappingStructureData(profile.detail.properties, Detail, isCouple),
      // myLooks: MemberProfile.mappingPropertyObject(Detail.MyLooks),
      generalInterests: Detail.GeneralInterests?.map((item: any) => MemberProfile.mappingPropertyObject(item)) || [],
      sexualInterests: Detail.SexualInterests?.map((item: any) => MemberProfile.mappingPropertyObject(item)) || [],
      sexualPreferences: Detail.SexualPreferences?.map((item: any) => MemberProfile.mappingPropertyObject(item)) || [],
      fetishInterests: Detail.FetishInterests?.map((item: any) => MemberProfile.mappingPropertyObject(item)) || [],
      // ...user.detail.generalInterests, ...user.detail.fetishInterests, ...user.detail.sexualInterests, ...user.detail.sexualPreferences
      profileInterests: MemberProfile.mappingCommonInterest([
        ...(Detail?.GeneralInterests || []),
        ...(Detail?.FetishInterests || []),
        ...(Detail?.SexualInterests || []),
        ...(Detail?.SexualPreferences || []),
      ]),
      fantasyActivity: MemberProfile.mappingPropertyObject(Detail.FantasyActivity),
      fantasyLocation: MemberProfile.mappingPropertyObject(Detail.FantasyLocation),
      fantasyMustHave: MemberProfile.mappingPropertyObject(Detail.FantasyMustHave),
      country: MemberProfile.mappingMemberCountry(Detail.UserCountry),
      isViewable: utils.hasOwnProperty(Detail, 'IsViewable') ? Detail.IsViewable : true,
      shareProfileCode: Detail.ShareProfileCode || null,
    };
    profile.profileSeeking = {
      seekingBody: utils.convertEnterStringToTagHTML(Seeking.SeekingBody),
      stateID: Seeking.StateID,
      stateName: Seeking.StateName,
      ageMin: Seeking.AgeMin,
      ageMax: Seeking.AgeMax,
      memberTypes: Seeking.MemberTypes?.map((item: any) => MemberProfile.mappingPropertyObject(item)) || [],
      intention: (Seeking.Intention && Seeking.Intention.map((item: any) => MemberProfile.mappingPropertyObject(item))) || [],
      dealBreaker: (Seeking.DealBreakers && Seeking.DealBreakers.map((item: any) => MemberProfile.mappingPropertyObject(item))) || [],
      relationshipStatus: MemberProfile.mappingPropertyObject(Seeking.RelationshipStatus),
    };
    profile.profileIntersection = {
      isMatch: Intersection.IsMatch,
      isFriend: Intersection.IsFriend,
      hasFriendRequest: Intersection.HasFriendRequest,
      hasFriendRequestDeleted: Intersection.HasFriendRequestDeleted,
      isLiked: Intersection.HasLiked,
      isLikedMutual: Intersection.IsLikeMutual,
      isFavorited: Intersection.HasFavourited,
      isFavouritedViewer: !!Intersection.HasFavouritedViewer,
      isFavoritedMutual: Intersection.IsFavouriteMutual,
      hasValidationRequest: Intersection.HasValidationRequest,
      hasMessages: Intersection.HasMessages,
      canAddValidation: Intersection.CanAddValidation,
      canViewValidations: Intersection.CanViewValidations,
      hasFavouritedViewer: Intersection.HasFavouritedViewer,
      hasLikedViewer: Intersection.HasLikedViewer,
      dealBreakerConflict: DealBreaker.mappingObjects(
        Intersection && Intersection.DealBreakerConflict ? Intersection.DealBreakerConflict : []
      ),
      dealBreakerConflictViewer: DealBreaker.mappingObjects(
        Intersection && Intersection.DealBreakerConflictViewer ? Intersection.DealBreakerConflictViewer : []
      ),
    };
    profile.mainPhotoSummary = MemberProfile.mappingMainPhoto(MainPhoto);
    profile.listItem = {
      itemDate: ListItem ? utils.extractDateToUnix(ListItem.ItemDate) : null,
    };
    profile.isNew = IsNew || null;
    profile.isProfileNotFound = false;
    return profile;
  }

  static mappingMainPhoto(mainPhoto: any): IMainPhoto {
    return {
      photoID: mainPhoto.PhotoID,
      height: mainPhoto.Height,
      width: mainPhoto.Width,
      isUserImage: mainPhoto.IsUserImage,
      isPublic: mainPhoto.IsPublic,
      classification: mainPhoto.Classification,
      thumbUrl: mainPhoto.ThumbUrl,
      fullUrl: mainPhoto.FullUrl,
      imageType: mainPhoto.ImageType,
      imageActionType: mainPhoto.ImageActionType,
      showSafeMode: mainPhoto.ShowSafeMode,
    };
  }

  static mappingObjectsCustomMemberSearch(_users: any[]): IMemberProfile[] {
    const users: IMemberProfile[] = [];
    _users.forEach(_user => {
      const user = MemberProfile.mappingObject(_user.MemberProfile || _user);
      users.push(user);
    });
    return users;
  }
}
